<script setup lang="ts">
import { computed } from 'vue';
import FeatureBubble from './FeatureBubble.vue';

type Anchor =
  | 'top'
  | 'bottom'
  | 'start'
  | 'end'
  | 'top start'
  | 'top end'
  | 'bottom start'
  | 'bottom end'
  | 'start top'
  | 'start bottom'
  | 'end top'
  | 'end bottom';

export type CoverFeatureItem = {
  id: string;
  type: string;
  icon?: string;
  iconSize?: number;
  tooltip?: string;
  tooltipLocation?: Anchor;
  color?: string;
  textColor?: string;
  content?: string;
  data?: any;
};

type Props = {
  features?: CoverFeatureItem[];
  direction?: 'horizontal' | 'vertical';
  position?: 'left' | 'right' | 'top' | 'bottom';
  size?: 'small' | 'medium' | 'large';
  iconSize?: number;
  rounded?: boolean;
  elevated?: boolean;
  extraItemClasses?: string[];
};

const props = withDefaults(defineProps<Props>(), {
  features: () => [],
  direction: 'horizontal',
  position: 'right',
  size: 'medium',
  iconSize: 16, // 18 is invalid for v-icon, so we set it to 16
  rounded: true,
  elevated: true,
});

const sizeMap: Record<string, number> = {
  small: 24,
  large: 32,
};

const featureSize = computed(() => sizeMap[props.size] ?? 28);

const featureBgColorMap: Record<CoverFeatureItem['type'], string> = {
  externalMaterial: 'bg-warning',
  teachingMaterial: 'bg-info',
  noAudio: 'bg-error',
  bookTalk: 'bg-success',
  languages: 'bg-secondary',
  downloaded: 'bg-inverse-surface',
  grade: 'bg-secondary-container',
  category: '', // CategoryBubble handles its own styling
  isFullTextBook: 'bg-secondary-container',
  isSpeakingBook: 'bg-inverse-surface',
};

// Get background color class for a feature
const getBackgroundClass = (feature: CoverFeatureItem) => {
  if (feature.color) {
    return '';
  }

  // Default background colors based on feature type
  return featureBgColorMap[feature.type] ?? 'bg-surface-variant';
};
</script>

<template>
  <ul
    class="book-feature ma-0 pa-0 position-absolute d-flex ga-1 gr-1"
    :class="{
      'book-features': true,
      [`position-${position}`]: true,
      [`direction-${direction}`]: true,
    }"
  >
    <!-- Default slot before features -->
    <slot name="before"></slot>

    <!-- Loop through features -->
    <template v-for="(feature, index) in features" :key="feature.id || index">
      <!-- Use named slot if provided -->
      <slot :name="feature.type" :feature="feature" :size="featureSize" :icon-size="iconSize">
        <!-- Default feature rendering -->
        <li
          :class="[
            getBackgroundClass(feature),
            extraItemClasses,
            { rounded: rounded, 'elevation-1': elevated, 'rounded-sm': !rounded },
          ]"
          :style="{
            backgroundColor: feature.color,
            color: feature.textColor,
            width: `${featureSize}px`,
            height: `${featureSize}px`,
          }"
          class="d-flex align-center justify-center"
        >
          <FeatureBubble :feature="feature" :icon-size="props.iconSize" />
        </li>
      </slot>
    </template>

    <!-- Default slot after features -->
    <slot name="after"></slot>

    <!-- Default slot for custom features -->
    <slot></slot>
  </ul>
</template>

<style scoped lang="scss">
.book-features {
  list-style: none;
  z-index: 5;

  li {
    box-sizing: border-box;
    padding: 2px;

    .v-icon {
      vertical-align: top;
    }

    span {
      font-size: 12px;
    }
  }
}

.book-feature {
  aspect-ratio: 1;
}

.feature-border {
  border: 1px solid rgb(var(--v-theme-outline));
}

/* Direction styles */
.direction-horizontal {
  display: flex;
  flex-direction: row;

  button {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }
}

.direction-vertical {
  display: flex;
  flex-direction: column;
  transform: translateX(-50%);
  top: -8px !important;
  left: 0 !important;
}

/* Position styles */
.position-right {
  top: -3px !important;
  right: -3px !important;

  &.direction-horizontal {
    li {
      border-radius: 2px 2px 15px 15px !important;
      box-shadow: 1px 1px 1px 1px rgba(12, 49, 78, 0.3) !important;

      &:last-child {
        border-radius: 2px 10px 2px 15px !important;
      }
    }
    li.rounded-sm {
      &:last-child {
        border-radius: 2px 2px 2px 15px !important;
      }
    }
  }
}

.position-left {
  top: -3px !important;
  left: -3px !important;

  &.direction-horizontal {
    li {
      border-radius: 2px 15px 15px 2px !important;

      &:last-child {
        border-radius: 15px 2px 10px 2px !important;
      }
    }
    li.rounded-sm {
      &:last-child {
        border-radius: 15px 2px 2px 2px !important;
      }
    }
  }
}

.position-top {
  top: -3px;
  left: 50%;
  transform: translateX(-50%);
}

.position-bottom {
  bottom: -3px;
  left: 50%;
  transform: translateX(-50%);
}
</style>
